/**
 * Provides the authenticated user's identity and role to the component tree.
 *
 * The context object and its accessor hook live in ./currentUser so this file
 * exports the provider alone and stays a React Fast Refresh boundary.
 *
 * The query key ['auth','me'] maps to GET /api/v1/auth/me/ and is kept
 * staleTime:Infinity because role changes arrive via WebSocket — polling
 * would add noise and miss nothing that a WS event wouldn't catch first.
 *
 * Three lifecycle events drive invalidation: auth_user_role_changed,
 * auth_user_disabled, auth_user_enabled. Matching is on user_id
 * (cc_sec_users.id) — never entra_oid — to follow the backend payload contract.
 *
 * On own auth_user_disabled the user is signed out through the same flow as
 * SignOutButton to avoid a wall of 403s (UXR-6).
 *
 * A WS authz_denied handshake refusal (policy-level, not a transient error)
 * surfaces the awaitingGrant flag so the UI can explain the state rather than
 * silently breaking.
 */

import React, { useEffect, useState, useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useMsal, useIsAuthenticated } from '@azure/msal-react'
import { api } from '@/services/api'
import { websocketManager } from '@/services/websocketCompat'
import { webSocketService } from '@/services/websocket'
import { CurrentUserContext } from './currentUser'
import type { MeResponse, AuthUserLifecyclePayload } from '@/types/auth'

interface CurrentUserProviderProps {
  children: React.ReactNode
}

export function CurrentUserProvider({
  children,
}: CurrentUserProviderProps): React.ReactElement {
  const queryClient = useQueryClient()
  const { instance } = useMsal()
  const isAuthenticated = useIsAuthenticated()
  const [awaitingGrant, setAwaitingGrant] = useState(false)

  const { data: user = null, isLoading } = useQuery<MeResponse | null>({
    queryKey: ['auth', 'me'],
    queryFn: () => api.auth.getMe(),
    // Only fetch once MSAL has confirmed an authenticated session.
    enabled: isAuthenticated,
    // Invalidation is entirely event-driven via WS; polling adds no value.
    staleTime: Infinity,
  })

  // Signs the user out in order: DELETE session → disconnect socket → logoutRedirect.
  const performSignOut = useCallback(async (): Promise<void> => {
    try {
      await api.auth.deleteSession()
    } catch (err) {
      console.warn('CurrentUserContext: sign-out DELETE failed (best-effort):', err)
    }
    websocketManager.disconnect()
    await instance.logoutRedirect().catch((err) => {
      console.error('CurrentUserContext: logoutRedirect failed:', err)
    })
  }, [instance])

  // WS lifecycle subscriptions — invalidate on matching user_id, sign out on own disable.
  useEffect(() => {
    const invalidateMe = (): void => {
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] })
    }

    const handleLifecycle = (message: { data?: unknown }): void => {
      const payload = message.data as AuthUserLifecyclePayload | undefined
      if (!payload?.user_id || !user?.id) return
      if (payload.user_id !== user.id) return
      invalidateMe()
    }

    const handleDisabled = (message: { data?: unknown }): void => {
      const payload = message.data as AuthUserLifecyclePayload | undefined
      if (!payload?.user_id || !user?.id) return
      if (payload.user_id !== user.id) return
      // Sign out immediately — the account is disabled; further API calls
      // would 403 and the user would see a wall of errors.
      performSignOut()
    }

    // Grant-change: refetch /auth/me so effective_grants stays current.
    // The broadcast payload is slim ({user_id, action}) — never scope detail —
    // so the grants are read back through the authorised /auth/me endpoint.
    // Replay active interests against the server's refreshed grant snapshot.
    const handleGrantChanged = (message: { data?: unknown }): void => {
      const payload = message.data as { user_id?: string } | undefined
      if (!payload?.user_id || !user?.id) return
      if (payload.user_id !== user.id) return
      invalidateMe()
      // Re-subscribe project rooms so the server gate re-evaluates the new grants.
      webSocketService.refreshProjectSubscriptions()
    }

    const unsubRole = websocketManager.subscribe('auth_user_role_changed', handleLifecycle)
    const unsubEnabled = websocketManager.subscribe('auth_user_enabled', handleLifecycle)
    const unsubDisabled = websocketManager.subscribe('auth_user_disabled', handleDisabled)
    const unsubGrantChanged = websocketManager.subscribe(
      'auth_user_grant_changed',
      handleGrantChanged,
    )

    return () => {
      unsubRole()
      unsubEnabled()
      unsubDisabled()
      unsubGrantChanged()
    }
  }, [user?.id, queryClient, performSignOut])

  // Surface the awaiting-grant state when the WS handshake is refused at the
  // policy tier (authz_denied). This is not a transient error — the socket
  // has already stopped reconnecting in simpleWebSocket.ts.
  useEffect(() => {
    const unsub = websocketManager.onAuthzDenied(() => {
      setAwaitingGrant(true)
    })
    return unsub
  }, [])

  return (
    <CurrentUserContext.Provider value={{ user, isLoading, awaitingGrant }}>
      {children}
    </CurrentUserContext.Provider>
  )
}

CurrentUserProvider.displayName = 'CurrentUserProvider'
